import type { ReactNode } from "react";
import { cn } from "../lib/utils";

interface PageHeaderProps {
  /** Page title. Pass an already-translated string. */
  title: string;
  /** Optional muted subtitle below the title. */
  description?: string;
  /** Optional right-aligned actions slot (buttons, etc.). */
  actions?: ReactNode;
  className?: string;
}

/**
 * Standard page header: a large semibold title, an optional muted description,
 * and an optional right-aligned actions slot. Replaces the ad-hoc `<h1>` + `<p>`
 * blocks repeated across screens so every page shares the same top structure.
 *
 * Presentational only — pass already-translated strings.
 */
export function PageHeader({
  title,
  description,
  actions,
  className,
}: PageHeaderProps) {
  return (
    <div
      className={cn(
        "mb-8 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between",
        className,
      )}
    >
      <div className="space-y-1">
        <h1 className="text-[26px] font-bold tracking-[-0.02em] text-balance">
          {title}
        </h1>
        {description && (
          <p className="text-muted-foreground text-[13px] text-pretty">
            {description}
          </p>
        )}
      </div>
      {actions && (
        <div className="flex shrink-0 items-center gap-2">{actions}</div>
      )}
    </div>
  );
}

export type { PageHeaderProps };
